import type { QualityRisk } from '~/gql/quality/quality'
import { useQuery } from '@apollo/client/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GQL_QUALITY_RISKS } from '~/gql/quality/quality'
import TablePagination from '~/pages/backoffice/TablePagination'
import { useClientPagination } from '~/pages/backoffice/use-client-pagination'
import { downloadCSV } from '~/utils/exportCsv/utils'
import { risksCsv } from './quality-csv'
import QualityRegister from './QualityRegister'
import QualityRiskDialog from './QualityRiskDialog'
import QualityRisksTable from './QualityRisksTable'
import { useQualityMutations } from './use-quality-mutations'

/** Le registre des risques qualité (#328), du plus grave au moins grave. */
function QualityRisksPanel({ formatDate }: { formatDate: (value: unknown) => string }) {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(GQL_QUALITY_RISKS, { fetchPolicy: 'cache-and-network' })
  // `null` : aucun dialogue ; `undefined` : un nouveau risque.
  const [editing, setEditing] = useState<QualityRisk | undefined | null>(null)
  const mutations = useQualityMutations(() => {
    setEditing(null)
    void refetch()
  })
  const risks = [...(data?.qualityRisks ?? [])].sort((a, b) => b.severity - a.severity)
  const { pageRows, pagination } = useClientPagination(risks, '')

  const exportCsv = () => {
    const { headers, rows } = risksCsv(risks, t, formatDate)
    downloadCSV(t('quality.risks.fileName'), headers, rows)
  }

  return (
    <>
      <QualityRegister
        loading={loading && !data}
        count={risks.length}
        empty={t('quality.risks.empty')}
        newLabel={t('quality.risks.new.button')}
        onNew={() => setEditing(undefined)}
        onExport={exportCsv}
      >
        <QualityRisksTable risks={pageRows} formatDate={formatDate} onOpen={setEditing} />
        <TablePagination {...pagination} />
      </QualityRegister>
      {editing !== null && (
        <QualityRiskDialog
          risk={editing}
          busy={mutations.busy}
          onCancel={() => setEditing(null)}
          onSave={form => void (editing ? mutations.updateRisk(editing.id, form) : mutations.createRisk(form))}
        />
      )}
    </>
  )
}

export default QualityRisksPanel
